import { FC, useEffect } from 'react';
import { Field, useField } from 'react-final-form';
import { USER_FIELD_MAX_LENGTHS } from 'constants/userFieldMaxLengths';
import Spacing from 'components/Spacing';
import { BodySmall, UtilityText } from 'components/Typography';
import { TextFieldWithFloatingLabel } from 'components/FinalForm/TextFieldWithFloatingLabel';
import { EmploymentDetails } from './Employment';
import theme from 'config/theme';
import { DetailsColumn, DetailsRow, NameValidationError } from '../styled';
import { getUserState } from 'selectors/user';
import { useAppSelector } from 'helpers/hooks';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { CountrySelectField, StateSelectField } from 'components/FinalForm/AddressSelectFields';
import { LoginCTA } from './LoginCTA';
import { SecuredByStripe } from 'components/SecuredByStripe';
import {
  individualOnlyWarningFull,
  individualOnlyWarningShort,
  required,
  validateEmail,
  validateNameCheckout,
} from 'helpers/validation';

interface Props {
  candidates: CandidateType[];
  isEmployerAddressRequired: boolean;
  isRecurring?: boolean;
  onDetailsView?: () => void;
  showSecuredByStripe?: boolean;
}

export const Details: FC<Props> = ({
  candidates,
  isEmployerAddressRequired,
  isRecurring = false,
  onDetailsView,
  showSecuredByStripe = false,
}) => {
  const {
    input: { value: country },
  } = useField('country', { subscription: { value: true } });
  const {
    meta: { error: firstNameError },
  } = useField('first_name', { subscription: { error: true } });
  const {
    meta: { error: lastNameError },
  } = useField('last_name', { subscription: { error: true } });
  const userState = useAppSelector(getUserState);

  useEffect(() => {
    if (onDetailsView) {
      onDetailsView();
    }
  }, []);

  return (
    <div className="personal-details">
      <div className="details-header">
        <span className="body-text">Personal Details</span>
        {showSecuredByStripe && <SecuredByStripe />}
      </div>
      {(firstNameError === individualOnlyWarningShort ||
        lastNameError === individualOnlyWarningShort) && (
        <NameValidationError>
          <BodySmall $color={theme.colors.errorRed}>{individualOnlyWarningFull}</BodySmall>
        </NameValidationError>
      )}
      <DetailsColumn>
        {userState?.data?.email ? (
          <Field name="email" component="input" hidden />
        ) : (
          <>
            <TextFieldWithFloatingLabel
              name="email"
              label="Email"
              validate={validateEmail}
              maxLength={USER_FIELD_MAX_LENGTHS.email}
            />
            <LoginCTA candidates={candidates} isCheckout={true} isRecurring={isRecurring} />
          </>
        )}
        <DetailsRow>
          <TextFieldWithFloatingLabel
            label="First Name"
            name="first_name"
            validate={validateNameCheckout}
            maxLength={USER_FIELD_MAX_LENGTHS.first_name}
          />
          <TextFieldWithFloatingLabel
            name="last_name"
            label="Last Name"
            validate={validateNameCheckout}
            maxLength={USER_FIELD_MAX_LENGTHS.last_name}
          />
        </DetailsRow>
        <TextFieldWithFloatingLabel
          name="address1"
          label="Address 1"
          validate={required}
          maxLength={USER_FIELD_MAX_LENGTHS.address1}
        />
        <TextFieldWithFloatingLabel
          name="address2"
          label="Address 2"
          maxLength={USER_FIELD_MAX_LENGTHS.address2}
        />
        <DetailsRow>
          <TextFieldWithFloatingLabel
            name="city"
            label="City"
            validate={required}
            maxLength={USER_FIELD_MAX_LENGTHS.city}
          />
          <StateSelectField
            country={country}
            label="State"
            labelRegion="Region"
            name="state"
            validate={required}
          />
        </DetailsRow>
        <DetailsRow>
          <TextFieldWithFloatingLabel
            name="zipcode"
            label="Zip"
            validate={required}
            maxLength={USER_FIELD_MAX_LENGTHS.zipcode}
          />
          <CountrySelectField label="Country" name="country" usOnly={true} />
        </DetailsRow>
      </DetailsColumn>
      <UtilityText $color={theme.shadows.black(0.6)}>
        *All fields are required unless otherwise noted.
      </UtilityText>

      <Spacing $size={15} />

      <EmploymentDetails isEmployerAddressRequired={isEmployerAddressRequired} />

      <Spacing $size={24} />
    </div>
  );
};
